import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { LoadingState, EmptyState } from "@/components/ui/StateBoundaries";
import * as purchaseApi from "@/lib/purchaseApi";
import { inr } from "@/lib/format";
import { exportReport } from "@/utils/exports";

/**
 * What came in and what went back (owner, 2026-11-01): by supplier, item, department
 * or day, valued at the purchase line's cost and tax, on the day it arrived.
 */
const GROUPS = [["supplier", "Supplier"], ["item", "Item"], ["department", "Department"], ["day", "Day"]];
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA"); };
const today = () => new Date().toLocaleDateString("en-CA");
const qty = value => Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function PurchaseReports({ token }) {
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [group, setGroup] = useState("supplier");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    purchaseApi.purchaseReport({ fromDate, toDate, group }, token).then(setData).catch(err => setError(reason(err)));
  }, [fromDate, toDate, group, token]);

  const download = format => {
    try {
      const rows = data.rows.map(r => ({
        [GROUPS.find(g => g[0] === group)[1]]: r.label,
        ...(group === "item" ? { Unit: r.unit, Bought: r.quantity, Returned: r.returned_quantity } : {}),
        Value: r.value, Tax: r.tax, "Returned value": r.returned, Net: r.net,
      }));
      exportReport(format, { purchases: rows }, `purchases-by-${group}-${fromDate}-to-${toDate}`);
    } catch (err) {
      toast.error(`Not downloaded: ${err.message}`);
    }
  };

  return (
    <section className="space-y-base" data-testid="purchase-reports">
      <div className="flex flex-wrap items-center gap-snug">
        <DatePicker value={fromDate} max={toDate} onChange={v => setFromDate(v || monthStart())} ariaLabel="From" testId="purchase-report-from" />
        <DatePicker value={toDate} min={fromDate} onChange={v => setToDate(v || today())} ariaLabel="To" testId="purchase-report-to" />
        <div className="flex gap-tight" role="tablist">
          {GROUPS.map(([key, text]) => (
            <Button key={key} size="sm" variant={group === key ? "default" : "outline"} role="tab" aria-selected={group === key}
                    onClick={() => setGroup(key)} data-testid={`purchase-report-group-${key}`}>By {text.toLowerCase()}</Button>
          ))}
        </div>
        {data?.rows.length > 0 && (
          <div className="ml-auto flex gap-tight">
            <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid="purchase-report-csv"><Download className="w-4 h-4" />CSV</Button>
            <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="purchase-report-xlsx"><Download className="w-4 h-4" />Excel</Button>
          </div>
        )}
      </div>
      {error && <p className="text-meta text-danger" role="alert">Purchase report not available: {error}</p>}
      {!error && !data && <LoadingState message="Adding up purchases…" />}
      {data && data.rows.length === 0 && (
        <EmptyState title="Nothing bought in these days" message="No goods arrived and none went back in this range." testId="purchase-report-empty" />
      )}
      {data && data.rows.length > 0 && (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-body">
            <thead className="bg-muted/50 text-meta text-muted-foreground">
              <tr>
                <th className="px-base py-snug text-left font-medium">{GROUPS.find(g => g[0] === group)[1]}</th>
                {group === "item" && <th className="px-base py-snug text-right font-medium">Bought</th>}
                <th className="px-base py-snug text-right font-medium">Value</th>
                <th className="px-base py-snug text-right font-medium">Tax</th>
                <th className="px-base py-snug text-right font-medium">Returned</th>
                <th className="px-base py-snug text-right font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-t" data-testid={`purchase-report-row-${i}`}>
                  <td className="px-base py-snug">{r.label}</td>
                  {group === "item" && (
                    <td className="px-base py-snug text-right tabular-nums">
                      {qty(r.quantity)} {r.unit}{r.returned_quantity ? ` (−${qty(r.returned_quantity)})` : ""}
                    </td>
                  )}
                  <td className="px-base py-snug text-right tabular-nums">{inr(r.value)}</td>
                  <td className="px-base py-snug text-right tabular-nums">{inr(r.tax)}</td>
                  <td className="px-base py-snug text-right tabular-nums">{r.returned ? inr(r.returned) : "—"}</td>
                  <td className="px-base py-snug text-right tabular-nums font-semibold">{inr(r.net)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 font-semibold">
              <tr>
                <td className="px-base py-snug">Total</td>
                {group === "item" && <td />}
                <td className="px-base py-snug text-right tabular-nums">{inr(data.totals.value)}</td>
                <td className="px-base py-snug text-right tabular-nums">{inr(data.totals.tax)}</td>
                <td className="px-base py-snug text-right tabular-nums">{inr(data.totals.returned)}</td>
                <td className="px-base py-snug text-right tabular-nums" data-testid="purchase-report-net">{inr(data.totals.net)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
